import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import htmlParser from "react-html-parser";
import "./TreeDetail.css";
import "./../TreeInfoCard/TreeInfoCard.css";
import "./../TreeInfo/TreeInfo.css";

// action creators
import { getTreeDetail } from "../../actions/treeDetail";
import { Cone_Trees, PAGE_TOP_DESKTOP } from "../../utilities/constants";
import { getUserListsThunk } from "../../actions/list";
import { convertPH } from "../../utilities/conversions";

// components
import TreeNameInfo from "../TreeNameInfo/TreeNameInfo";
import TreeDetailGallery from "../TreeDetailGallery/TreeDetailGallery";
import SearchByNameSection from "../SearchByNameSection/SearchByNameSection";
import ResponsiveAccordion from "../ResponsiveAccordion/ResponsiveAccordion";
import ErrorPage from "../ErrorPage/ErrorPage";
import CopyButton from "../CopyButton/CopyButton";

import RoleGate from "../RoleGate/RoleGate";
import createGlossaryLink from "../../utilities/glossaryLinker";

export default function TreeDetail() {
  const [loading, setLoading] = useState(true);
  const params = useParams();
  const tree_id = parseInt(params.treeName);
  const dispatch = useDispatch();
  const tree_detail = useSelector(state => state.treeDetail);
  const user = useSelector(state => state.user);

  function renderList(array, end_str = " and ") {
    return array.reduce((accum, v, idx) => {
      if (idx === 0) return accum + v;
      return accum + (idx < array.length - 1 ? ", " : end_str) + v;
    }, "");
  }

  function TreeAttribute(props) {
    const { name, name_override, value, useOr, html, boolean } = props;

    try {
      let attribute_value = null;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const end_str = useOr ? " or " : " and ";
          attribute_value = renderList(value, end_str);
        }
      } else if (boolean && value !== null) {
        attribute_value = value ? "Yes" : "No";
      } else {
        attribute_value = value;
      }

      if (!attribute_value) {
        console.log(
          `components.treeDetailV2.TreeAttribute ${name}: missing value`
        );
        return "";
      }

      function render_name() {
        if (name || name_override) {
          return (
            <span>
              {name_override ? name_override : createGlossaryLink(name)}:{" "}
            </span>
          );
        }
      }

      function render_body() {
        if (html) {
          return (
            <span
              dangerouslySetInnerHTML={{
                __html: attribute_value,
              }}
            ></span>
          );
        } else {
          return <span>{attribute_value}</span>;
        }
      }

      return (
        <span className="tree-info-card-text">
          {render_name()}
          {render_body()}
        </span>
      );
    } catch (err) {
      console.log(`components.treeDetailV2.TreeAttribute ${name}: ${err}`);
      return "";
    }
  }

  function TreeInfoCard(props) {
    return (
      <Col lg={6} xs={12} className="mb-4">
        <ResponsiveAccordion title={props.title}>
          <Container
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {props.children}
          </Container>
        </ResponsiveAccordion>
      </Col>
    );
  }

  function AttributeGroup(props) {
    const has_children =
      props.children &&
      props.children.find(v => {
        return (
          v.props &&
          v.props.value !== null &&
          v.props.value !== "" &&
          (!Array.isArray(v.props.value) || v.props.value.length)
        );
      });

    if (has_children) {
      return <div className="tree-info-attribute-group">{props.children}</div>;
    } else {
      return "";
    }
  }

  useEffect(() => {
    try {
      dispatch(getTreeDetail(tree_id)).then(res => setLoading(false));
    } catch (err) {
      console.log(`components.TreeDetail.useEffect: ${err}`);
    }
  }, [params]);

  useEffect(() => {
    try {
      //If User is logged in fetch user lists
      if (user && user.id) {
        dispatch(getUserListsThunk());
      }
    } catch (err) {
      console.log(`components.TreeDetail.useEffect: ${err}`);
    }
  }, [user]);

  if (loading) {
    return (
      <Container xs={{ span: 4, offset: 4 }} className="text-center">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (!tree_detail || Array.isArray(tree_detail)) {
    return (
      <Container xs={{ span: 4, offset: 4 }} className="text-center">
        <ErrorPage
          message={"There are no trees found that match that query."}
        />
      </Container>
    );
  }

  //Helper Function for Rendering Ranges between related attributes
  function render_range(low, high, unit = "", prefix = "", seperator = "") {
    if (low || high) {
      let str = "";
      let sep = seperator ? seperator : "-";
      if (low === high) {
        str = low;
      } else {
        str = low + (low && high ? sep : "") + high;
      }
      return `${prefix}${str}${unit ? "" + unit : ""}`;
    } else {
      return null;
    }
  }

  //Extra Proccessing for more advanced render logic
  //Construct potential_invasiveness text
  let potential_invasiveness = null;
  if (tree_detail.invasive_text) {
    potential_invasiveness = tree_detail.invasive_text;
  } else if (tree_detail.pi_invasive || tree_detail.ca_invasive) {
    potential_invasiveness =
      "Invasive in " +
      (tree_detail.pi_invasive ? "Hawaii" : "") +
      (tree_detail.pi_invasive && tree_detail.ca_invasive ? " and  " : "") +
      (tree_detail.ca_invasive ? "California" : "");
  }

  //Advanced Logic for fruit text
  let cone_flag = Cone_Trees.includes(tree_detail.family);
  let fruit_str = null;
  if (tree_detail.fruit_size) {
    fruit_str = tree_detail.fruit_size;
  }
  if (tree_detail.fruit_color) {
    if (fruit_str) fruit_str += " ";
    fruit_str += tree_detail.fruit_color;
  }
  if (tree_detail.fruit_type) {
    if (fruit_str) fruit_str += " ";
    fruit_str += tree_detail.fruit_type;
  }

  //Advanced Logic for Contsuction of appraisal_text
  let appraisal_text = null;
  if (tree_detail.appraisal) {
    if (tree_detail.app_lcant === "Use BTF") {
      appraisal_text = `${tree_detail.app_lcant}, ${tree_detail.appraisal_text}`;
    } else if (
      tree_detail.appraisal_text.includes(
        `Suggested LCANT ${tree_detail.app_lcant}`
      )
    ) {
      appraisal_text = tree_detail.appraisal_text;
    } else {
      appraisal_text = `Suggested LCANT ${tree_detail.app_lcant}, ${tree_detail.appraisal_text}`;
    }
  }

  //Planting Area View
  let planting_area_str = null;
  if (tree_detail.planting_area) {
    if (tree_detail.planting_area.includes(">")) {
      var new_plantingArea = tree_detail.planting_area.split(">");
      var new_val = new_plantingArea[1].trim();
      planting_area_str = `Greater than ${new_val}'`;
    } else if (tree_detail.planting_area.includes("to")) {
      var new_plantingArea = tree_detail.planting_area.split("to");
      var low_val = new_plantingArea[0].trim();
      var high_val = new_plantingArea[1].trim();
      planting_area_str = `${low_val}' to  ${high_val}'`;
    } else if (tree_detail.planting_area.includes("-")) {
      var new_plantingArea = tree_detail.planting_area.split("-");
      var low_val = new_plantingArea[0].trim();
      var high_val = new_plantingArea[1].trim();
      planting_area_str = `${low_val}' to  ${high_val}'`;
    }
  }

  let citation_text = null;
  if (tree_detail.primary_taxon && tree_detail.primary_taxon.name_concat) {
    const sciname = tree_detail.primary_taxon.name_concat.replace(
      /(<([^>]+)>)/gi,
      ""
    );

    const date = new Date();
    const year = date.getFullYear();
    const formatDate = new Intl.DateTimeFormat("default", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);

    citation_text = `"SelecTree. UFEI. "${sciname} Tree Record." 1995-${year}. Cal Poly State University, San Luis Obispo. Accessed on ${formatDate}."`;
  }

  return (
    <Container>
      <Container fluid className="tree-detail--tree-detail-gallery pb-4 mb-4">
        <Container fluid className="tree-detail--tree-name-info">
          <Container>
            <TreeNameInfo treeDetail={tree_detail} />
          </Container>
        </Container>
        <Container>
          <TreeDetailGallery treeDetail={tree_detail} />
        </Container>
      </Container>
      <Container fluid className="tree-detail--tree-info">
        <Container>
          <Row>
            <TreeInfoCard title="GENERAL INFO">
              <TreeAttribute value={htmlParser(tree_detail.memo)} html />
              <TreeAttribute
                name="Native Range"
                value={tree_detail.native_range}
              />
              <TreeAttribute
                name="Potential invasiveness"
                value={potential_invasiveness}
              />
              <TreeAttribute
                name="Horticultural use"
                value={[
                  ...tree_detail.landscape_application,
                  ...tree_detail.landscape_use,
                ]}
                useOr
              />
              <TreeAttribute
                name="Ethnobotanical Info / Tree Use"
                value={
                  htmlParser(tree_detail.ethnobotanical_info) +
                  " <a href='https://ufei.calpoly.edu/references/'>Citations Page</a>"
                }
                html
              />
              <TreeAttribute
                name="Schoolyard Use"
                value={
                  tree_detail.schoolyard
                    ? htmlParser(tree_detail.schoolyard_use_notes)
                    : ""
                }
                html
              />
            </TreeInfoCard>

            <TreeInfoCard title="TREE CHARACTERISTICS">
              <AttributeGroup>
                <TreeAttribute
                  name="Tree Shape"
                  value={tree_detail.tree_shape}
                  useOr
                />
                <TreeAttribute
                  name="Foliage Type"
                  value={["Deciduous", "Partly Deciduous", "Evergreen"].find(
                    (value, index) =>
                      value === tree_detail.foliage_type ||
                      `${index}` === tree_detail.foliage_type
                  )}
                />
              </AttributeGroup>
              <AttributeGroup>
                <TreeAttribute
                  name="Maximum Tree Height"
                  value={
                    tree_detail.height_high
                      ? tree_detail.height_high + " feet"
                      : null
                  }
                />
                <TreeAttribute
                  name="Canopy Width"
                  value={render_range(
                    tree_detail.width_low,
                    tree_detail.width_high,
                    "feet"
                  )}
                />
                <TreeAttribute
                  name="Growth Rate"
                  value={render_range(
                    tree_detail.growth_rate_low,
                    tree_detail.growth_rate_high,
                    "in/year",
                    "~"
                  )}
                />
              </AttributeGroup>
              <AttributeGroup>
                <TreeAttribute
                  name_override={
                    <span>
                      {createGlossaryLink("Leaf Arrangement")} and{" "}
                      {createGlossaryLink("Form", "Leaf Form")}
                    </span>
                  }
                  value={[tree_detail.leaf_arrangement, tree_detail.leaf_form]}
                />
                <TreeAttribute
                  name="Leaf Shape"
                  value={tree_detail.leaflet_shape}
                />
                <TreeAttribute
                  name="Leaf Color"
                  value={tree_detail.foliage_growth_color}
                  useOr
                />
                <TreeAttribute
                  name="Fall Color"
                  boolean={true}
                  value={tree_detail.foliage_fall_color}
                />
              </AttributeGroup>
              <AttributeGroup>
                <TreeAttribute
                  name={cone_flag ? "Cones" : "Flowers"}
                  value={
                    tree_detail.flower_showiness || tree_detail.fragrance
                      ? tree_detail.flower_showiness +
                        (tree_detail.flower_showiness && tree_detail.fragrance
                          ? ", "
                          : "") +
                        (tree_detail.fragrance ? "Fragrant" : "")
                      : null
                  }
                />
                <TreeAttribute
                  name="Flower Color"
                  value={tree_detail.flower_color}
                  useOr
                />
                <TreeAttribute
                  name={cone_flag ? "Reproduction" : "Flower Type"}
                  value={tree_detail.sex_explanation}
                  useOr
                />
                <TreeAttribute
                  name="Flower Time"
                  value={!cone_flag ? tree_detail.flower_time : null}
                  useOr
                />
              </AttributeGroup>

              <AttributeGroup>
                <TreeAttribute
                  name_override={
                    <span>
                      {createGlossaryLink(cone_flag? "Cone Size" : "Fruit Size", "Fruit Size")}{" "}
                      and{" "}
                      {createGlossaryLink("Color", "Fruit Color")}
                    </span>
                  }
                  value={fruit_str}
                />
                <TreeAttribute
                  name={cone_flag ? "Pollen time" : "Fruiting Time"}
                  value={tree_detail.fruiting_time}
                  useOr
                />
                <TreeAttribute
                  name="Fruit Value"
                  value={tree_detail.fruit_value}
                />
              </AttributeGroup>

              <AttributeGroup>
                <TreeAttribute
                  name="Bark Color"
                  value={tree_detail.bark_color}
                />
                <TreeAttribute
                  name="Bark Texture"
                  value={tree_detail.bark_texture}
                />
              </AttributeGroup>
              <TreeAttribute
                name="Litter Type"
                value={tree_detail.litter_type}
              />
            </TreeInfoCard>

            <TreeInfoCard title="SITE CONDITIONS">
              <TreeAttribute
                name="Recommended Planting Area"
                value={planting_area_str}
              />
              <TreeAttribute
                name="Water Use Rating"
                value={tree_detail.water_use}
              />
              <TreeAttribute
                name="Sunset Climate Zone"
                value={tree_detail.formatted_sunset_zones}
              />
              <TreeAttribute
                name="USDA Hardiness Zone"
                value={tree_detail.usda_zones}
              />
              <TreeAttribute
                name="Soil Texture"
                value={tree_detail.soil_texture}
                useOr
              />
              <TreeAttribute
                name="Soil pH"
                value={
                  tree_detail.soil_ph_low && tree_detail.soil_ph_high
                    ? convertPH(tree_detail.soil_ph_low) +
                      " to " +
                      convertPH(tree_detail.soil_ph_high)
                    : null
                }
              />
              <TreeAttribute
                name="Salt Tolerance"
                value={tree_detail.salt_tolerance}
              />
              <TreeAttribute
                name="Schoolyard Trees"
                value={tree_detail.schoolyard}
                boolean
              />
              <TreeAttribute
                name="Shade Tolerance"
                value={tree_detail.shade_tolerant}
                boolean
              />
              {/* Hidden for now whilst data is being cleaned up <TreeAttribute
                name="Climate adapted"
                value={tree_detail.climate_adapted_regions}
              />*/}
            </TreeInfoCard>

            <TreeInfoCard title="CONSIDERATIONS">
              <TreeAttribute
                name="Branch Strength"
                value={render_range(
                  tree_detail.branch_strength_low,
                  tree_detail.branch_strength_high,
                  "",
                  "",
                  " to "
                )}
              />
              <TreeAttribute
                name="Root Damage Potential"
                value={tree_detail.root_damage_potential}
              />
              <TreeAttribute
                name="Potential health issues"
                value={tree_detail.health_hazard}
              />
              <TreeAttribute
                name="Biogenic Emissions"
                value={tree_detail.biogenic_emissions}
              />
              <TreeAttribute
                name="Wildlife Interactions"
                value={
                  (tree_detail.deer_palatable === 1
                    ? "Tree is deer resistant"
                    : "") +
                  (tree_detail.deer_palatable && tree_detail.attracts_wildlife
                    ? " and "
                    : "") +
                  (tree_detail.attracts_wildlife
                    ? "Attracts " + renderList(tree_detail.attracts_wildlife)
                    : "")
                }
              />

              <TreeAttribute
                name_override={
                  <span>
                    {createGlossaryLink("Disease", "Disease Susceptibility")} and{" "}
                    {createGlossaryLink("Pest Susceptibility")}
                  </span>
                }
                value={[
                  ...tree_detail.disease_susceptibility,
                  ...tree_detail.pest_susceptibility,
                ]}
              />
              <TreeAttribute
                name_override={
                  <span>
                    {createGlossaryLink("Disease", "Disease Resistance")} and{" "}
                    {createGlossaryLink("Pest Resistance")}
                  </span>
                }
                value={[
                  ...tree_detail.disease_resistant,
                  ...tree_detail.pest_resistant,
                ]}
              />
              <TreeAttribute
                name={"Utility Friendly"}
                value={tree_detail.utility_friendly}
                boolean
              />
              <TreeAttribute
                name={<a href="/appraisals">WCISA Appraisal</a>}
                value={appraisal_text}
              />
            </TreeInfoCard>
            {tree_detail.external_resources && (
              <TreeInfoCard title="EXTERNAL RESOURCES">
                {tree_detail.external_resources.map(resource => {
                  return resource.href !== "N/A" && resource.href !== null ? (
                    <span className="tree-info-card-text">
                      <a href={resource.host + resource.href} target="_blank">
                        {resource.name}
                      </a>
                    </span>
                  ) : (
                    ""
                  );
                })}
              </TreeInfoCard>
            )}

            {citation_text && (
              <TreeInfoCard title="CITE THIS TREE">
                <span className="tree-info-card-text-cite">
                  {citation_text}
                  <br />
                  {`< https://selectree.calpoly.edu/tree-detail/${tree_detail.tree_id} >`}
                  <CopyButton copy_text={citation_text} size={20} />
                </span>
              </TreeInfoCard>
            )}
          </Row>
        </Container>
      </Container>
      <RoleGate allowed_roles={[2, 3]}>
        <div style={{ textAlign: "center", marginTop: ".5rem" }}>
          <Link
            className="form-button-primary"
            to={`/tree-detail/${tree_detail.tree_id}/edit`}
            onClick={() => window.scrollTo(PAGE_TOP_DESKTOP)}
          >
            Edit this Tree
          </Link>
        </div>
      </RoleGate>
      <div className="d-none d-lg-block">
        <Container>
          <hr />
        </Container>
        <SearchByNameSection />
      </div>
    </Container>
  );
}
